/**
 * Game over fullscreen overlay with stats, animations, and visual feedback.
 * "나가기" button lives in the action bar, not here.
 */

import type {
  PlayerId,
  StoneFlickingState,
} from "@cloudnest/augmented-stone-flicking-engine";
import { useEffect, useRef } from "react";
import { GameOverStats } from "./components/GameOverStats";

interface GameOverScreenProps {
  readonly gameState: StoneFlickingState;
  readonly myPlayerId: PlayerId | null;
  readonly onLeaveGame: () => void;
}

const REASON_LABELS: Record<string, string> = {
  ALL_ELIMINATED: "상대 기물을 모두 제거했습니다",
  TURN_LIMIT: "턴 제한 도달",
  CONCEDE: "항복",
};

type Outcome = "victory" | "defeat" | "draw";

function getOutcome(
  gameState: StoneFlickingState,
  myPlayerId: PlayerId | null,
): Outcome {
  if (gameState.isDraw) return "draw";
  return gameState.winner === myPlayerId ? "victory" : "defeat";
}

const OUTCOME_STYLES: Record<Outcome, { bg: string; accent: string }> = {
  victory: {
    bg: "rgba(26, 107, 62, 0.75)",
    accent: "var(--cn-asf-stone-selected-glow)",
  },
  defeat: {
    bg: "rgba(0, 0, 0, 0.7)",
    accent: "rgba(255,255,255,0.6)",
  },
  draw: {
    bg: "rgba(50, 50, 50, 0.7)",
    accent: "rgba(255,255,255,0.8)",
  },
};

const RESULT_TEXT: Record<Outcome, string> = {
  victory: "승리!",
  defeat: "패배",
  draw: "무승부",
};

export function GameOverScreen({
  gameState,
  myPlayerId,
  onLeaveGame,
}: GameOverScreenProps) {
  const outcome = getOutcome(gameState, myPlayerId);
  const styles = OUTCOME_STYLES[outcome];
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    overlayRef.current?.focus();
  }, []);

  const outcomeReason = gameState.coreState.outcomeReason;
  const reasonText = outcomeReason ? (REASON_LABELS[outcomeReason] ?? "") : "";

  return (
    <div
      ref={overlayRef}
      data-testid="game-over-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`게임 종료: ${RESULT_TEXT[outcome]}`}
      tabIndex={-1}
      onClick={onLeaveGame}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onLeaveGame();
      }}
      style={{
        position: "fixed",
        inset: 0,
        bottom: "56px",
        zIndex: 55,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        backgroundColor: styles.bg,
        cursor: "pointer",
        animation: "asf-gameover-fade 0.4s ease-out",
      }}
    >
      {/* Result text */}
      <div
        data-testid="winner-display"
        style={{
          fontSize: "32px",
          fontWeight: 800,
          color: styles.accent,
          textShadow: "0 2px 8px rgba(0,0,0,0.3)",
          animation:
            "asf-gameover-scale 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
      >
        {RESULT_TEXT[outcome]}
      </div>

      {/* Reason */}
      <div
        data-testid="victory-reason-display"
        style={{
          fontSize: "14px",
          color: "rgba(255,255,255,0.7)",
        }}
      >
        {reasonText}
      </div>

      {/* Stats card */}
      <GameOverStats gameState={gameState} myPlayerId={myPlayerId} />

      {/* Tap hint */}
      <div
        style={{
          fontSize: "12px",
          color: "rgba(255,255,255,0.4)",
          marginTop: "8px",
        }}
      >
        탭하여 닫기
      </div>
    </div>
  );
}
